// Request-signature helpers shared by the recorder (capture key) and the
// replay handler (lookup key). Both sides MUST produce identical signatures
// for the same logical request, so normalization lives here in one place.

// Query params whose value changes per viewer/session and would otherwise
// prevent a recorded fixture from matching on replay. Stripped from the key.
const VOLATILE_PARAMS = new Set(['timezoneoffsetminutes', 'offset']);

// Same-origin prefixes the demo shim is allowed to answer. Everything else
// (static assets, the fixtures bundle itself, third-party URLs) passes through
// to the real fetch untouched.
const API_PREFIXES = ['/api/', '/audio_lessons/', '/audiobooks/', '/epub_assets/'];

export type ParsedUrl = { path: string; search: URLSearchParams };

export function parseApiUrl(rawUrl: string): ParsedUrl | null {
  try {
    const u = new URL(rawUrl, window.location.origin);
    return { path: u.pathname, search: u.searchParams };
  } catch {
    return null;
  }
}

export function isApiUrl(rawUrl: string): boolean {
  const parsed = parseApiUrl(rawUrl);
  if (!parsed) return false;
  const p = parsed.path;
  return p === '/api' || API_PREFIXES.some((prefix) => p.startsWith(prefix));
}

export function isMediaUrl(rawUrl: string): boolean {
  const parsed = parseApiUrl(rawUrl);
  if (!parsed) return false;
  const p = parsed.path;
  return (
    p.startsWith('/audio_lessons/') ||
    p.startsWith('/audiobooks/') ||
    p.startsWith('/epub_assets/')
  );
}

// Normalized path with no trailing slash, lower-cased so the PascalCase swagger
// paths and the lower-case call sites collapse to one key.
export function normalizePath(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  return (trimmed || '/').toLowerCase();
}

// Stable "METHOD /path?sorted-query" key with volatile params removed.
export function signature(method: string, rawUrl: string): string {
  const parsed = parseApiUrl(rawUrl);
  if (!parsed) return `${method.toUpperCase()} ${rawUrl}`;
  const path = normalizePath(parsed.path);
  const entries = [...parsed.search.entries()]
    .map(([k, v]) => [k.toLowerCase(), v] as [string, string])
    .filter(([k]) => !VOLATILE_PARAMS.has(k))
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  const qs = entries.map(([k, v]) => `${k}=${v}`).join('&');
  return `${method.toUpperCase()} ${path}${qs ? `?${qs}` : ''}`;
}

// Path-only key, used as a fallback when an exact query match isn't recorded
// (e.g. the visitor changes a filter we never captured).
export function pathSignature(method: string, rawUrl: string): string {
  const parsed = parseApiUrl(rawUrl);
  if (!parsed) return `${method.toUpperCase()} ${rawUrl}`;
  return `${method.toUpperCase()} ${normalizePath(parsed.path)}`;
}
